/* Redo HSA/FSA cart badge -- native ajax-cart drawer integration for Baloo
 * Living (balooliving.com), YSW 1.0.0 theme. patch_v2, 2026-08-13.
 *
 * REBUILD, not a port -- this is a NEW cart-app family per
 * widget_factory/FACTORY.md's cart-side catalog: not UpCart, not EliteCart,
 * not Rebuy (all three confirmed absent via tools/pull_merchant_assets.mjs,
 * 2026-08-13: zero upcart/elitecart/rebuy markers on the live homepage).
 * Baloo runs the theme's OWN native ajax-cart drawer, rendered by a real
 * Shopify section (id="shopify-section-ajax-cart") the theme's own
 * section-ajax-cart.min.js already drives via the Section Rendering API --
 * the same mechanism this file's own data-map fetch uses, not a guess.
 *
 * WHAT PORTED VERBATIM from the proven UpCart (NEUBRAIN) / EliteCart (Uni)
 * builds, per FACTORY.md's "what actually varies per family" measurement:
 * the root-discovery scaffolding (shadow-root + same-origin-iframe walk),
 * the fetch/poll loop shape, and the badge-copy table shape. What's NEW:
 * the entire selector layer AND a second, Baloo-specific data source (see
 * "VARIANT-ID RESOLUTION" below) -- this merchant's cart provides no direct
 * per-item variant-id or line-key DOM attribute, unlike UpCart/EliteCart.
 *
 * SELECTOR CONFIDENCE -- read before editing this file. Same disclosure
 * convention as cart_patterns/elitecart_uni_frozen/redo-hsa-cart-badge.js.
 *
 *   - LINE_SELECTOR = "[data-cart-line]": OBSERVED. Confirmed live in the
 *     theme's own compiled section-ajax-cart.min.js (fetched public,
 *     unauthenticated, 2026-08-13): the script's quantity-change and
 *     remove-line handlers both do
 *       e = target.closest('[data-cart-line]'); value = e.getAttribute('data-cart-line')
 *     and POST that value as the `line` parameter to Shopify's OWN
 *     /cart/change.js endpoint. Shopify's Ajax Cart API documents `line` as
 *     the 1-based POSITION of the item in cart.items -- this is a first-
 *     party Shopify API semantic the theme is using as designed, not an
 *     app's internal, renamable convention (the EliteCart build's closest
 *     analogous inference had no such external contract to anchor it).
 *   - ITEM_DETAILS_SELECTOR = ".ajax-cart__item-details": OBSERVED. Real
 *     class in the theme's own compiled section-ajax-cart.min.css (fetched
 *     public, 2026-08-13): `.ajax-cart__item-details{display:grid;gap:.25rem}`,
 *     sibling to `.ajax-cart__item-title` / `.ajax-cart__item-price` in the
 *     same stylesheet -- reads as the per-item text column, a natural badge
 *     home. NOT confirmed against a live rendered item (empty-cart
 *     limitation below), so this script also has a same-class fallback (see
 *     "BADGE PLACEMENT").
 *   - CART_CONTAINER_SELECTOR = "#ajaxCart, .ajax-cart, .js-cart-items":
 *     OBSERVED. All three literal ids/classes appear in the live homepage
 *     HTML (fetched public, 2026-08-13): id="ajaxCart" (drawer root),
 *     class="ajax-cart" (same element), id="ajaxCartItems"
 *     class="ajax-cart__items js-cart-items" (the items container, matching
 *     the theme JS's own selector constant s.cartItems=".js-cart-items").
 *
 * VARIANT-ID RESOLUTION -- the one genuinely new mechanism this family
 * needs, because Baloo's DOM carries no per-item variant id or line-key
 * attribute at all (confirmed: zero data-variant-id / data-product-id hits
 * anywhere in the theme's compiled JS or CSS). Per the standing doctrine
 * ("a coder cannot get a live-rendered cart-drawer DOM capture from a
 * public fetch... reconstructing from the app's own JS/CSS bundle and
 * labeling inferred-vs-observed per selector is the correct fallback, not
 * a gap to apologize for"), this script cross-references TWO independently
 * OBSERVED facts instead of guessing a selector:
 *   1. Each [data-cart-line] element's own attribute value = the item's
 *      1-based position (OBSERVED, see above).
 *   2. GET /cart.js (Shopify's own standard, read-only, unauthenticated
 *      Ajax Cart endpoint -- confirmed 200 live on Baloo, 2026-08-13) returns
 *      cart.items[] in that SAME server-side order, each item carrying a
 *      real variant_id.
 * variantId = cart.items[dataCartLineValue - 1].variant_id. A SAFETY GATE
 * guards the one real risk in this inference (order/count ever
 * desynchronizing): if the number of [data-cart-line] elements in the DOM
 * does not exactly equal cart.items.length on a given apply() cycle, this
 * script treats EVERY line as unresolved for that cycle (removes any
 * existing badges, retries next poll) rather than risk mapping a badge to
 * the wrong line item. This could NOT be verified against a live rendered
 * drawer with real items (an anonymous session's cart is empty; populating
 * one needs a real browser session or a live /cart/add.js POST, both
 * outside a coder subagent's public-fetch-only, no-live-mutation lane) --
 * flagged as the runbook's first live-verification step.
 *
 * BADGE PLACEMENT: appended as the LAST CHILD of .ajax-cart__item-details
 * when present (OBSERVED class, reads naturally as a new row in that grid
 * column); falls back to appending as the last child of the
 * [data-cart-line] element itself when that inner class is absent --
 * lower visual precision, immune to any inner-class rename, same "immune
 * to renaming" tradeoff the EliteCart build already accepted.
 *
 * DATA SOURCE: sections/redo-hsa-cart-data.liquid (this package's
 * cart_badge/redo-hsa-cart-data.liquid), fetched via the Section Rendering
 * API (GET {root}?sections=redo-hsa-cart-data), unchanged from the UpCart/
 * EliteCart builds -- see that file's own header comment.
 *
 * COPY: flat "HSA eligible." for EVERY eligible line item, both the
 * `eligible` and `eligible_with_lmn` resolved values sharing one string --
 * NOT the UpCart build's original two-tier copy ("HSA/FSA accepted." /
 * "HSA/FSA eligible for qualified customers."). Per the 08-10 badge-copy
 * lock (feedback_badge_copy_hsa_eligible_factory_standard,
 * "eligible for qualified customers" is the exact forbidden euphemism) AND
 * this build's own brief, which names the deliverable "per-line 'HSA
 * eligible'" directly. Also matches Baloo's own PDP card/button, which
 * carry ONE flat "HSA/FSA eligible" claim with no tier differentiation
 * across their whole 18-handle catalog today -- a cart badge that DID
 * differentiate would contradict what the SAME product's PDP already told
 * the shopper. run_battery.mjs's own "flat-label merchant" convention
 * (LABEL_LMN === LABEL_ELIGIBLE by design) is the precedent for this being
 * a supported, not improvised, shape.
 *
 * INSTALL:
 *   1. sections/redo-hsa-cart-data.liquid
 *   2. This file as assets/redo-hsa-cart-badge.js
 *   3. In layout/theme.liquid, just before </body>:
 *        <script src="{{ 'redo-hsa-cart-badge.js' | asset_url }}" defer="defer"></script>
 */
(() => {
  if (window.__redoHsaCartBadgeActive) return; /* run-once guard */
  window.__redoHsaCartBadgeActive = true;

  const LINE_SELECTOR = "[data-cart-line]";
  const ITEM_DETAILS_SELECTOR = ".ajax-cart__item-details";
  const CART_CONTAINER_SELECTOR = "#ajaxCart, .ajax-cart, .js-cart-items";
  const BADGE_CLASS = "redo-hsa-lineitem-badge";
  const SECTION_ID = "redo-hsa-cart-data";
  const FETCH_COOLDOWN_MS = 4000;
  const CART_FETCH_COOLDOWN_MS = 4000;
  const POLL_MS = 2000;
  const CHECK_GLYPH = "✓"; /* check mark */
  const CHECK_COLOR = "#2c374e"; /* Baloo's own CTA navy, brand_tokens.json cta_navy -- same accent the PDP card/button already use, not a generic green */
  const COPY = {
    eligible: "HSA eligible.",
    eligible_with_lmn: "HSA eligible.",
  };

  let map = {};
  let fetchInFlight = null;
  let lastFetchAt = 0;
  let noProgressFetches = 0;

  let cartItemsOrdered = null; /* Shopify /cart.js items[], server order */
  let cartFetchInFlight = null;
  let lastCartFetchAt = 0;

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function fetchMap() {
    if (fetchInFlight) return fetchInFlight;
    if (noProgressFetches >= 3) return Promise.resolve(map);
    const now = Date.now();
    if (now - lastFetchAt < FETCH_COOLDOWN_MS) return Promise.resolve(map);
    lastFetchAt = now;
    fetchInFlight = fetch(rootUrl() + "?sections=" + SECTION_ID)
      .then((r) => r.json())
      .then((json) => {
        const html = json && json[SECTION_ID];
        if (!html) {
          noProgressFetches += 1;
          return map;
        }
        const doc = new DOMParser().parseFromString(html, "text/html");
        const el = doc.querySelector("[data-redo-hsa-cart-map]");
        if (!el) {
          noProgressFetches += 1;
          return map;
        }
        const next = JSON.parse(el.textContent);
        const changed = JSON.stringify(next) !== JSON.stringify(map);
        map = next;
        noProgressFetches = changed ? 0 : noProgressFetches + 1;
        return map;
      })
      .catch((err) => {
        console.error("Redo HSA cart badge: eligibility map fetch failed", err);
        noProgressFetches += 1;
        return map;
      })
      .finally(() => {
        fetchInFlight = null;
      });
    return fetchInFlight;
  }

  /* Baloo-specific: Shopify's own standard, read-only /cart.js endpoint.
     Gives cart.items[] in the same server-side order the theme's own
     data-cart-line positions assume. See header comment "VARIANT-ID
     RESOLUTION" for the full reasoning and the safety gate this feeds. */
  function fetchCartItems() {
    if (cartFetchInFlight) return cartFetchInFlight;
    const now = Date.now();
    if (now - lastCartFetchAt < CART_FETCH_COOLDOWN_MS) return Promise.resolve(cartItemsOrdered);
    lastCartFetchAt = now;
    cartFetchInFlight = fetch(rootUrl() + "cart.js")
      .then((r) => r.json())
      .then((json) => {
        cartItemsOrdered = (json && Array.isArray(json.items)) ? json.items : [];
        return cartItemsOrdered;
      })
      .catch((err) => {
        console.error("Redo HSA cart badge: /cart.js fetch failed", err);
        return cartItemsOrdered;
      })
      .finally(() => {
        cartFetchInFlight = null;
      });
    return cartFetchInFlight;
  }

  /* ---- root discovery across isolation boundaries ----
   * Ported verbatim from the UpCart/EliteCart builds. Baloo's own
   * pull_merchant_assets.mjs recon found no shadowMount-*.js modulepreload
   * (unlike Uni's EliteCart), so this is defensive coverage, not load-
   * bearing today -- kept per FACTORY.md's own claim this scaffolding is
   * cart-app-agnostic and ports verbatim, zero cost if unused. */

  let cartRoots = [];
  const observedRoots = new WeakSet();

  function collectRoots(root, acc) {
    acc.push(root);
    let all;
    try {
      all = root.querySelectorAll("*");
    } catch (e) {
      return;
    }
    for (const el of all) {
      if (el.shadowRoot) collectRoots(el.shadowRoot, acc);
      if (el.tagName === "IFRAME") {
        try {
          if (el.contentDocument) collectRoots(el.contentDocument, acc);
        } catch (e) {
          /* cross-origin iframe -- unreachable, skip */
        }
      }
    }
  }

  function rootIsAlive(r) {
    if (r === document) return true;
    if (r.nodeType === 9) {
      try {
        const frame = r.defaultView && r.defaultView.frameElement;
        return !!(frame && frame.isConnected);
      } catch (e) {
        return false;
      }
    }
    return !!(r.host && r.host.isConnected);
  }

  function observeRoot(r) {
    if (observedRoots.has(r)) return;
    try {
      const target = r.nodeType === 9 ? r.documentElement || r : r;
      new MutationObserver(schedule).observe(target, { childList: true, subtree: true });
      observedRoots.add(r);
    } catch (e) {
      /* leave unobserved; the safety poll still covers this root */
    }
  }

  function ensureRoots() {
    cartRoots = cartRoots.filter(rootIsAlive);
    if (cartRoots.length === 0) {
      const acc = [];
      collectRoots(document, acc);
      cartRoots = acc.filter((r) => {
        try {
          return r.querySelector(LINE_SELECTOR) || r.querySelector(CART_CONTAINER_SELECTOR);
        } catch (e) {
          return false;
        }
      });
      cartRoots.forEach(observeRoot);
    }
    return cartRoots;
  }

  /* ---- badge rendering ---- */

  function makeBadge(copyKey, ownerDoc) {
    const doc = ownerDoc || document;
    const badge = doc.createElement("div");
    badge.className = BADGE_CLASS;
    badge.setAttribute("data-redo-hsa-eligibility", copyKey);
    badge.style.cssText =
      "display:flex;align-items:flex-start;gap:5px;" +
      "margin:4px 0 0;font-family:Söhne,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:.75rem;" +
      "font-weight:400;line-height:1.4;color:#363741;";

    const icon = doc.createElement("span");
    icon.textContent = CHECK_GLYPH;
    icon.setAttribute("aria-hidden", "true");
    icon.style.cssText =
      "color:" + CHECK_COLOR + ";font-weight:700;font-size:.8125rem;" +
      "line-height:inherit;flex-shrink:0;";

    const text = doc.createElement("span");
    text.textContent = COPY[copyKey];

    badge.appendChild(icon);
    badge.appendChild(text);
    return badge;
  }

  function badgeHomeFor(tile) {
    return tile.querySelector(ITEM_DETAILS_SELECTOR) || tile;
  }

  /* Idempotent: safe to run on every DOM change; settles with no
     mutations. Per line item -- each tile gets its own badge or none. */
  function apply() {
    if (window.Shopify && window.Shopify.country && window.Shopify.country !== "US") {
      return;
    }
    let sawUnknownVariant = false;
    for (const root of ensureRoots()) {
      let tiles;
      try {
        tiles = root.querySelectorAll(LINE_SELECTOR);
      } catch (e) {
        continue;
      }
      if (tiles.length === 0) continue;

      /* SAFETY GATE -- see header "VARIANT-ID RESOLUTION". If the DOM's
         line count and the fetched cart's item count disagree, positional
         mapping is untrustworthy this cycle: clear any existing badges in
         this root rather than risk mismapping one, and ask for a fresh
         /cart.js read. */
      if (!cartItemsOrdered || cartItemsOrdered.length !== tiles.length) {
        root.querySelectorAll("." + BADGE_CLASS).forEach((b) => b.remove());
        sawUnknownVariant = true;
        continue;
      }

      tiles.forEach((tile) => {
        try {
          const lineAttr = tile.getAttribute("data-cart-line");
          const lineIdx = parseInt(lineAttr, 10);
          const home = badgeHomeFor(tile);
          const existing = home.querySelector("." + BADGE_CLASS);

          if (!lineAttr || isNaN(lineIdx) || lineIdx < 1 || lineIdx > cartItemsOrdered.length) {
            if (existing) existing.remove();
            return;
          }

          const cartItem = cartItemsOrdered[lineIdx - 1];
          const variantId = cartItem && cartItem.variant_id;
          if (!variantId) {
            if (existing) existing.remove();
            return;
          }

          const eligibility = map[String(variantId)];
          if (eligibility === undefined) {
            /* map hasn't caught up to this line yet -- leave any existing
               badge alone and ask for a fresh map, don't flap it off. */
            sawUnknownVariant = true;
            return;
          }

          if (eligibility !== "eligible" && eligibility !== "eligible_with_lmn") {
            if (existing) existing.remove();
            return;
          }

          if (!existing) {
            home.appendChild(makeBadge(eligibility, home.ownerDocument));
          } else if (existing.getAttribute("data-redo-hsa-eligibility") !== eligibility) {
            const replacement = makeBadge(eligibility, home.ownerDocument);
            existing.replaceWith(replacement);
          }
        } catch (e) {
          /* one malformed tile never takes the rest of the cart down */
        }
      });
    }
    if (sawUnknownVariant) {
      fetchCartItems().then(schedule);
      fetchMap().then(schedule);
    }
  }

  /* Coalesce observer bursts into one apply() per frame; apply() is
     idempotent so self-triggered mutations settle immediately. */
  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      apply();
    });
  }

  function init() {
    observeRoot(document);
    setInterval(schedule, POLL_MS);
    Promise.all([fetchMap(), fetchCartItems()]).then(schedule);
  }

  /* Debugging helpers. */
  window.redoHsaCartBadgeRefresh = function () {
    lastFetchAt = 0;
    lastCartFetchAt = 0;
    noProgressFetches = 0;
    cartRoots = [];
    Promise.all([fetchMap(), fetchCartItems()]).then(schedule);
  };
  window.redoHsaCartBadgeDebug = function () {
    const roots = ensureRoots();
    return {
      map: map,
      cartItemsOrdered: cartItemsOrdered,
      roots: roots.map((r) =>
        r === document
          ? "top-document"
          : r.nodeType === 9
          ? "iframe-document"
          : "shadow-root(" + (r.host && r.host.tagName) + "#" + (r.host && r.host.id) + ")"
      ),
      lines: roots.reduce((n, r) => n + r.querySelectorAll(LINE_SELECTOR).length, 0),
      badges: roots.reduce((n, r) => n + r.querySelectorAll("." + BADGE_CLASS).length, 0),
    };
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
